<template>
  <el-form
    v-show="getShow"
    ref="formLogin"
    :model="loginData.loginForm"
    :rules="LoginRules"
    class="login-form"
    label-position="top"
    label-width="120px"
    size="large"
  >
    <el-row class="mx-[-10px]">
      <el-col :span="24" class="px-10px">
        <el-form-item>
          <LoginFormTitle class="w-full" />
        </el-form-item>
      </el-col>
      <el-col :span="24" class="px-10px">
        <el-form-item v-if="loginData.tenantEnable === 'true'" prop="tenantName">
          <el-input
            v-model="loginData.loginForm.tenantName"
            :placeholder="t('login.tenantNamePlaceholder')"
            :prefix-icon="iconHouse"
            :disabled="tenantLockedByWebsite"
            link
            type="primary"
            @input="clearLoginError"
          />
        </el-form-item>
      </el-col>
      <el-col :span="24" class="px-10px">
        <el-form-item prop="username">
          <el-input
            v-model="loginData.loginForm.username"
            :placeholder="t('login.usernamePlaceholder')"
            :prefix-icon="iconAvatar"
            @input="clearLoginError"
          />
        </el-form-item>
      </el-col>
      <el-col :span="24" class="px-10px">
        <el-form-item prop="password">
          <el-input
            v-model="loginData.loginForm.password"
            :placeholder="t('login.passwordPlaceholder')"
            :prefix-icon="iconLock"
            show-password
            type="password"
            @input="clearLoginError"
            @keyup.enter="getCode()"
          />
        </el-form-item>
      </el-col>
      <el-col v-if="loginErrorMsg" :span="24" class="px-10px">
        <div class="login-error-tip">
          <Icon icon="ep:circle-close-filled" :size="15" />
          <span>{{ loginErrorMsg }}</span>
        </div>
      </el-col>
      <el-col
        :span="24"
        class="px-10px mb-[-20px]"
        :class="loginErrorMsg ? 'mt-[-4px]' : 'mt-[-20px]'"
      >
        <el-form-item>
          <el-row justify="space-between" style="width: 100%">
            <el-col :span="6">
              <el-checkbox
                v-model="loginData.loginForm.rememberMe"
                :disabled="!canRememberMe"
              >
                {{ t('login.remember') }}
              </el-checkbox>
            </el-col>
            <el-col :offset="6" :span="12">
              <el-link
                class="float-right"
                type="primary"
                @click="setLoginState(LoginStateEnum.RESET_PASSWORD)"
              >
                {{ t('login.forgetPassword') }}
              </el-link>
            </el-col>
          </el-row>
        </el-form-item>
      </el-col>
      <el-col :span="24" class="px-10px">
        <el-form-item>
          <el-button :loading="loginLoading" class="w-full" type="primary" @click="getCode()">
            {{ t('login.login') }}
          </el-button>
        </el-form-item>
      </el-col>
      <Verify
        v-if="loginData.captchaEnable === 'true'"
        ref="verify"
        :captchaType="captchaType"
        :imgSize="{ width: '400px', height: '200px' }"
        mode="pop"
        @success="handleLogin"
      />
      <el-col :span="24" class="px-10px">
        <el-form-item>
          <el-row :gutter="5" justify="space-between" style="width: 100%">
            <el-col :span="8">
              <el-button class="w-full" @click="setLoginState(LoginStateEnum.MOBILE)">
                {{ t('login.btnMobile') }}
              </el-button>
            </el-col>
            <el-col :span="8">
              <el-button class="w-full" @click="setLoginState(LoginStateEnum.QR_CODE)">
                {{ t('login.btnQRCode') }}
              </el-button>
            </el-col>
            <el-col :span="8">
              <el-button class="w-full" @click="setLoginState(LoginStateEnum.REGISTER)">
                {{ t('login.btnRegister') }}
              </el-button>
            </el-col>
          </el-row>
        </el-form-item>
      </el-col>
      <el-divider content-position="center">{{ t('login.otherLogin') }}</el-divider>
      <el-col :span="24" class="px-10px">
        <el-form-item>
          <div class="w-full flex justify-between">
            <Icon
              v-for="(item, key) in socialList"
              :key="key"
              :icon="item.icon"
              :size="30"
              class="anticon cursor-pointer"
              color="#999"
              @click="doSocialLogin(item.type)"
            />
          </div>
        </el-form-item>
      </el-col>
    </el-row>
  </el-form>
</template>
<script lang="ts" setup>
import LoginFormTitle from './LoginFormTitle.vue'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

import { useIcon } from '@/hooks/web/useIcon'

import * as authUtil from '@/utils/auth'
import { usePermissionStore } from '@/store/modules/permission'
import * as LoginApi from '@/api/login'
import { parseRouteLocation } from '@/utils/routeParams'
import { LoginStateEnum, useFormValid, useLoginState } from './useLogin'

defineOptions({ name: 'LoginForm' })

const { t } = useI18n()
const message = useMessage()
const iconHouse = useIcon({ icon: 'ep:house' })
const iconAvatar = useIcon({ icon: 'ep:avatar' })
const iconLock = useIcon({ icon: 'ep:lock' })
const formLogin = ref()
const { validForm } = useFormValid(formLogin)
const { setLoginState, getLoginState } = useLoginState()
const { currentRoute, push } = useRouter()
const permissionStore = usePermissionStore()
const redirect = ref<string>('')
const loginLoading = ref(false)
const loginErrorMsg = ref('')
const verify = ref()
const captchaType = ref('blockPuzzle') // blockPuzzle 滑块 clickWord 点击文字 pictureWord 文字验证码
/** 域名已识别到租户时锁定输入，避免手改与当前站点不一致 */
const tenantLockedByWebsite = ref(false)

const getShow = computed(() => unref(getLoginState) === LoginStateEnum.LOGIN)

const LoginRules = {
  tenantName: [{ required: true, message: t('common.required') }],
  username: [{ required: true, message: t('common.required') }],
  password: [{ required: true, message: t('common.required') }]
}
const loginData = reactive({
  isShowPassword: false,
  captchaEnable: import.meta.env.VITE_APP_CAPTCHA_ENABLE,
  tenantEnable: import.meta.env.VITE_APP_TENANT_ENABLE,
  loginForm: {
    tenantName: import.meta.env.VITE_APP_DEFAULT_LOGIN_TENANT || '',
    username: import.meta.env.VITE_APP_DEFAULT_LOGIN_USERNAME || '',
    password: import.meta.env.VITE_APP_DEFAULT_LOGIN_PASSWORD || '',
    captchaVerification: '',
    rememberMe: false
  }
})

/** 租户（开启时）+ 用户名 + 密码都填了才能勾选「记住我」 */
const canRememberMe = computed(() => {
  const form = loginData.loginForm
  const hasUsername = !!form.username?.trim()
  const hasPassword = !!form.password
  if (loginData.tenantEnable === 'true') {
    return !!form.tenantName?.trim() && hasUsername && hasPassword
  }
  return hasUsername && hasPassword
})

watch(canRememberMe, (ok) => {
  if (!ok) {
    loginData.loginForm.rememberMe = false
  }
})

const socialList = [
  { icon: 'ant-design:wechat-filled', type: 30 },
  { icon: 'ant-design:dingtalk-circle-filled', type: 20 },
  { icon: 'ant-design:github-filled', type: 0 },
  { icon: 'ant-design:alipay-circle-filled', type: 0 }
]

const clearLoginError = () => {
  loginErrorMsg.value = ''
}

/** 是否走滑块验证码（仅字符串 'false' 视为关闭，与后端 yudao.captcha.enable 对齐） */
const isCaptchaOn = () => String(loginData.captchaEnable) !== 'false'

const showCaptchaPopup = async () => {
  // 确保 Verify 已挂载（v-if 依赖 captchaEnable）
  if (!isCaptchaOn()) {
    loginData.captchaEnable = 'true'
  }
  await nextTick()
  if (!verify.value?.show) {
    message.warning('验证码组件未加载，请刷新页面后重试')
    return
  }
  verify.value.show()
}

// 获取验证码 / 登录入口
const getCode = async () => {
  // 前端关闭验证码：直接登录（需后端 yudao.captcha.enable=false，否则会提示验证码不能为空）
  if (!isCaptchaOn()) {
    await handleLogin({})
    return
  }
  // 已开启：先弹滑块，通过后再登录（验证码是弹层，不是表单里的输入框）
  await showCaptchaPopup()
}
// 获取租户 ID
const getTenantId = async () => {
  if (loginData.tenantEnable === 'true') {
    const res = await LoginApi.getTenantIdByName(loginData.loginForm.tenantName)
    authUtil.setTenantId(res)
  }
}
// 记住我：回填用户名/密码；租户名优先走域名识别，见 getTenantByWebsite
const getLoginFormCache = () => {
  const loginForm = authUtil.getLoginForm()
  if (loginForm) {
    loginData.loginForm = {
      ...loginData.loginForm,
      username: loginForm.username ? loginForm.username : loginData.loginForm.username,
      password: loginForm.password ? loginForm.password : loginData.loginForm.password,
      rememberMe: loginForm.rememberMe,
      tenantName: loginForm.tenantName ? loginForm.tenantName : loginData.loginForm.tenantName
    }
  }
}

/** 按当前域名回填租户名并锁定，与 RegisterForm / WeComEntry 一致 */
const getTenantByWebsite = async () => {
  if (loginData.tenantEnable !== 'true') {
    return
  }
  const res = await LoginApi.getTenantByWebsite(location.host)
  if (res?.name) {
    loginData.loginForm.tenantName = res.name
    authUtil.setTenantId(res.id)
    tenantLockedByWebsite.value = true
  }
}
// 登录
const handleLogin = async (params: any) => {
  loginLoading.value = true
  loginErrorMsg.value = ''
  try {
    await getTenantId()
    const data = await validForm()
    if (!data) {
      return
    }
    const loginDataLoginForm = { ...loginData.loginForm }
    // 租户走 header（getTenantId）；rememberMe 仅本地缓存，勿塞进 body
    const res = await LoginApi.login({
      username: loginDataLoginForm.username,
      password: loginDataLoginForm.password,
      captchaVerification: params.captchaVerification
    })
    if (!res) {
      return
    }
    if (loginDataLoginForm.rememberMe) {
      authUtil.setLoginForm(loginDataLoginForm)
    } else {
      authUtil.removeLoginForm()
    }
    authUtil.setAdminToken(res)
    if (!redirect.value) {
      redirect.value = '/home'
    }
    // 判断是否为SSO登录
    if (redirect.value.indexOf('sso') !== -1) {
      window.location.href = window.location.href.replace('/portal?redirect=', '')
    } else {
      // path 不能带 ?query，否则独立窗回跳会丢 standalone/app
      const target = redirect.value || permissionStore.addRouters[0].path
      await push(parseRouteLocation(target))
    }
  } catch (err) {
    // 登录接口错误在密码框下方展示，其他已由 Axios 拦截器处理的错误不重复提示。
    const msg = err instanceof Error ? String(err.message || '') : ''
    if (msg && msg !== 'error') {
      loginErrorMsg.value = msg
    }
    // 前端关了验证码、后端仍开启时：自动打开滑块，避免「没填验证码却报验证码不能为空」
    if (msg.includes('验证码') && String(loginData.captchaEnable) === 'false') {
      loginErrorMsg.value = '服务端已开启验证码，请完成下方滑块验证'
      loginLoading.value = false
      await showCaptchaPopup()
      return
    }
  } finally {
    loginLoading.value = false
  }
}

// 社交登录
const doSocialLogin = async (type: number) => {
  if (type === 0) {
    message.error('此方式未配置')
  } else {
    loginLoading.value = true
    if (loginData.tenantEnable === 'true') {
      // 尝试先通过 tenantName 获取租户
      await getTenantId()
      // 如果获取不到，则需要弹出提示，进行处理
      if (!authUtil.getTenantId()) {
        try {
          const data = await message.prompt('请输入企业名称', t('common.reminder'))
          if (data?.action !== 'confirm') throw 'cancel'
          const res = await LoginApi.getTenantIdByName(data.value)
          authUtil.setTenantId(res)
        } catch (error) {
          if (error === 'cancel') return
        } finally {
          loginLoading.value = false
        }
      }
    }
    // 计算 redirectUri
    // 注意: type、redirect 需要先 encode 一次，否则钉钉回调会丢失。
    // 配合 social-login.vue#getUrlValue() 使用
    const redirectUri =
      location.origin +
      '/social-login?' +
      encodeURIComponent(`type=${type}&redirect=${redirect.value || '/home'}`)

    // 进行跳转
    window.location.href = await LoginApi.socialAuthRedirect(type, encodeURIComponent(redirectUri))
  }
}
watch(
  () => currentRoute.value,
  (route: RouteLocationNormalizedLoaded) => {
    redirect.value = route?.query?.redirect as string
  },
  {
    immediate: true
  }
)
onMounted(() => {
  getLoginFormCache()
  getTenantByWebsite().catch(() => {
    // 域名识别失败时保留缓存/环境默认值，不阻塞登录
  })
})
</script>

<style lang="scss" scoped>
:deep(.anticon) {
  &:hover {
    color: var(--el-color-primary) !important;
  }
}

.login-code {
  float: right;
  width: 100%;
  height: 38px;

  img {
    width: 100%;
    height: auto;
    max-width: 100px;
    vertical-align: middle;
    cursor: pointer;
  }
}

.login-error-tip {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  margin: -10px 0 12px;
  padding: 8px 12px;
  color: #dc2626;
  font-size: 13px;
  line-height: 1.4;
  background: #fff7f7;
  border: 1px solid #fecaca;
  border-radius: 6px;
  box-sizing: border-box;
}

.login-error-tip span {
  min-width: 0;
  word-break: break-word;
}
</style>
